import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { API_BASE } from '../config/api';

/**
 * LegacyFilmRedirect — Phase 1 Task 4.2 (route consolidation).
 *
 * The workspace-prefixed routes (`/workspaces/:workspaceId/films/:filmId/...`)
 * are canonical — the app's architecture is workspace-centric (entitlements,
 * pricing, and context are all keyed by workspace). Legacy unprefixed film
 * URLs (`/films/:filmId/...`, old bookmarks / shared links) resolve the
 * film's workspace via GET /films/:id and redirect, preserving the subpath
 * (board, scenes, shots, manage, ai-breakdown, ...) and query string.
 *
 * Fallbacks:
 * - film not found / no access  -> home
 * - film has no workspace_id    -> home (post-migration this shouldn't happen)
 */
const LegacyFilmRedirect = () => {
  const params = useParams();
  const filmId = params.filmId;
  const splat = params['*'] || '';
  const location = useLocation();
  const [workspaceId, setWorkspaceId] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/films/${filmId}`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        if (r.data?.workspace_id) {
          setWorkspaceId(r.data.workspace_id);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filmId]);

  if (failed) return <Navigate to="/" replace />;

  if (workspaceId) {
    const sub = splat ? `/${splat}` : '';
    return (
      <Navigate
        to={`/workspaces/${workspaceId}/films/${filmId}${sub}${location.search}`}
        replace
      />
    );
  }

  return (
    <div
      className="container mx-auto px-4 md:px-6 py-12 text-muted-foreground text-sm"
      data-testid="legacy-film-redirect"
    >
      Redirecting…
    </div>
  );
};

export default LegacyFilmRedirect;
